import React, { useEffect, useState } from "react";
import { getCustomTable, saveCustomTable } from "./happyschoolApi";
import { getActiveSchoolId } from "./activeSchool";

// Văn thư lưu trữ: danh sách LINK tài liệu của trường (Google Sheets/Docs,
// biên bản, báo cáo...) — không lưu file, chỉ lưu đường dẫn + tác giả để tra
// cứu nhanh. Dùng chung hạ tầng "bảng tuỳ biến" (customTables) như các bảng
// khác, nhưng có giao diện riêng để link bấm mở được trực tiếp.

const TABLE_KEY = "vanThuLuuTru";
const COLUMNS = [
  { key: "tieude", label: "Tiêu đề tài liệu", width: 260 },
  { key: "link", label: "Link", width: 260 },
  { key: "tacgia", label: "Tác giả", width: 140 },
  { key: "ngay", label: "Ngày", width: 110 },
  { key: "ghichu", label: "Ghi chú", width: 200 },
];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const newId = () => `vt_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
// CHỈ chuẩn hoá hoa/thường + khoảng trắng — KHÔNG bỏ dấu, vì bỏ dấu sẽ gộp
// nhầm các tên khác nhau (VD "Đào Thị Phương" và "Đào Thị Phượng" thành 1).
const normName = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();

// `myName` (tuỳ chọn): chế độ "văn thư của tôi" — dùng trong dashboard giáo
// viên/CBNV. Vẫn tải TOÀN BỘ danh sách (để lưu lại không mất dữ liệu người
// khác), nhưng chỉ HIỂN THỊ + cho thêm tài liệu đứng tên chính người đó
// (khoá cứng "Tác giả" = myName, không sửa được thành tên khác).
const HappySchoolVanThu = ({ myName } = {}) => {
  const schoolId = getActiveSchoolId();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const emptyForm = { tieude: "", link: "", tacgia: myName || "", ngay: todayStr(), ghichu: "" };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!schoolId) { setLoading(false); return; }
    setLoading(true);
    getCustomTable(schoolId, TABLE_KEY)
      .then((d) => setRows(Array.isArray(d.rows) ? d.rows : []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [schoolId]);

  const persist = (newRows) => {
    setRows(newRows);
    setSaving(true);
    saveCustomTable(schoolId, TABLE_KEY, { columns: COLUMNS, rows: newRows })
      .catch((e) => setError(e.message))
      .finally(() => setSaving(false));
  };

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setShowForm(true); };
  const openEdit = (r) => { setForm({ tieude: r.tieude || "", link: r.link || "", tacgia: r.tacgia || "", ngay: r.ngay || "", ghichu: r.ghichu || "" }); setEditingId(r.id); setShowForm(true); };
  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); };

  const submit = (e) => {
    e.preventDefault();
    const tieude = form.tieude.trim();
    const link = form.link.trim();
    if (!tieude || !link) { setError("Cần nhập Tiêu đề và Link."); return; }
    setError("");
    const tacgia = myName || form.tacgia; // khoá cứng tác giả = chính mình khi ở chế độ "của tôi"
    if (editingId) {
      persist(rows.map((r) => (r.id === editingId ? { ...form, tieude, link, tacgia, id: editingId } : r)));
    } else {
      persist([...rows, { ...form, tieude, link, tacgia, id: newId() }]);
    }
    cancelForm();
  };

  const remove = (id) => {
    if (!window.confirm("Xoá tài liệu này khỏi danh sách?")) return;
    persist(rows.filter((r) => r.id !== id));
  };

  if (!schoolId) {
    return <div style={{ padding: 24 }}>Chưa chọn trường. Vào "Trường của tôi" trước.</div>;
  }

  const scoped = myName ? rows.filter((r) => normName(r.tacgia) === normName(myName)) : rows;
  const filtered = search.trim()
    ? scoped.filter((r) => [r.tieude, r.tacgia, r.ghichu].some((v) => String(v || "").toLowerCase().includes(search.trim().toLowerCase())))
    : scoped;
  const sorted = filtered.slice().sort((a, b) => String(b.ngay || "").localeCompare(String(a.ngay || "")));

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 4px" }}>🗄️ Văn thư lưu trữ{myName ? " của tôi" : ""}</h2>
      <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
        {myName
          ? "Danh sách link tài liệu do bạn đưa lên (Google Sheets, Docs, biên bản, báo cáo...). Bấm vào tiêu đề để mở link."
          : "Danh sách link tài liệu lưu trữ của trường (Google Sheets, Docs, biên bản, báo cáo...). Bấm vào tiêu đề để mở link."}
      </p>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Tìm theo tiêu đề, tác giả, ghi chú..."
          style={{ ...inputStyle, minWidth: 260 }}
        />
        <button type="button" onClick={openAdd} style={btn}>+ Thêm tài liệu</button>
        {saving && <span style={{ fontSize: 12, color: "#6b7280" }}>Đang lưu…</span>}
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}

      {showForm && (
        <form onSubmit={submit} style={{ ...card, marginBottom: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>{editingId ? "Sửa tài liệu" : "Thêm tài liệu mới"}</h3>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <label style={lbl}>Tiêu đề tài liệu *
              <input value={form.tieude} onChange={(e) => setForm((f) => ({ ...f, tieude: e.target.value }))} placeholder="VD: Danh sách HSSV đỗ ĐH-CĐ K23" style={inputStyle} />
            </label>
            <label style={{ ...lbl, flex: 1, minWidth: 260 }}>Link *
              <input value={form.link} onChange={(e) => setForm((f) => ({ ...f, link: e.target.value }))} placeholder="https://docs.google.com/..." style={inputStyle} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <label style={lbl}>Tác giả
              <input
                value={myName || form.tacgia}
                onChange={(e) => setForm((f) => ({ ...f, tacgia: e.target.value }))}
                placeholder="VD: Đào Thị Phương"
                disabled={!!myName}
                style={{ ...inputStyle, ...(myName ? { background: "#f3f4f6", color: "#6b7280" } : {}) }}
              />
            </label>
            <label style={lbl}>Ngày
              <input type="date" value={form.ngay} onChange={(e) => setForm((f) => ({ ...f, ngay: e.target.value }))} style={inputStyle} />
            </label>
            <label style={{ ...lbl, flex: 1, minWidth: 200 }}>Ghi chú
              <input value={form.ghichu} onChange={(e) => setForm((f) => ({ ...f, ghichu: e.target.value }))} style={inputStyle} />
            </label>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="submit" style={btn}>Lưu</button>
            <button type="button" onClick={cancelForm} style={btn2}>Huỷ</button>
          </div>
        </form>
      )}

      {loading ? (
        <p>Đang tải...</p>
      ) : sorted.length === 0 ? (
        <p style={{ color: "#9ca3af" }}>{scoped.length === 0 ? 'Chưa có tài liệu nào — bấm "+ Thêm tài liệu".' : "Không tìm thấy tài liệu khớp."}</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.map((r) => (
            <div key={r.id} style={{ ...card, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <a href={r.link} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 700, fontSize: 14, color: "#0f766e", textDecoration: "none" }}>
                  🔗 {r.tieude}
                </a>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {r.tacgia && <span>👤 {r.tacgia}</span>}
                  {r.ngay && <span>📅 {r.ngay}</span>}
                </div>
                {r.ghichu && <div style={{ fontSize: 12, color: "#374151", marginTop: 4 }}>{r.ghichu}</div>}
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button type="button" onClick={() => openEdit(r)} style={btn2}>Sửa</button>
                <button type="button" onClick={() => remove(r.id)} style={{ ...btn2, color: "#dc2626", borderColor: "#fecaca" }}>Xoá</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const inputStyle = { padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, boxSizing: "border-box", width: "100%" };
const lbl = { display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 600, color: "#4b5563" };
const btn = { padding: "8px 14px", background: "#0ea5a3", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 };
const btn2 = { padding: "6px 12px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 12 };
const card = { border: "1px solid #e5e7eb", borderRadius: 10, padding: 14, background: "#fff" };

export default HappySchoolVanThu;
